import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { petsApi } from '../../../api/pets.js';
import { categoriesApi } from '../../../api/categories.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

const EMPTY = {
  name: '',
  speciesId: '',
  breedId: '',
  sex: 'unknown',
  neutered: '',
  birthDate: '',
  weightKg: '',
  microchip: '',
  color: '',
  allergies: [],
  dietaryNotes: '',
  medicalNotes: '',
  status: 'active'
};

function dateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

export function PetFormModal({ open, onClose, customerId, initial }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();

  const speciesQuery = useQuery({ queryKey: ['cat', 'species'], enabled: open, queryFn: () => categoriesApi.list({ kind: 'species', limit: 100, active: true }) });
  const breedQuery = useQuery({ queryKey: ['cat', 'breed'], enabled: open, queryFn: () => categoriesApi.list({ kind: 'breed', limit: 100, active: true }) });
  const speciesOpts = useMemo(() => ((speciesQuery.data && speciesQuery.data.items) || []), [speciesQuery.data]);
  const breedOpts = useMemo(() => ((breedQuery.data && breedQuery.data.items) || []), [breedQuery.data]);

  const { register, handleSubmit, reset, control, watch, setValue, setError, formState: { errors } } = useForm({ defaultValues: EMPTY });

  // Breeds belong to a species (Category.parentId). Show only the breeds of the
  // currently selected species so the dropdown stays relevant.
  const watchedSpecies = watch('speciesId');
  const watchedBreed = watch('breedId');
  const filteredBreeds = useMemo(
    () => breedOpts.filter((b) => String(b.parentId?._id || b.parentId || '') === String(watchedSpecies || '')),
    [breedOpts, watchedSpecies]
  );

  // The species/breed option lists load asynchronously. A native <select>
  // cannot show a value whose <option> is not mounted yet, so resetting the form
  // before the lists arrive leaves the selects stuck on the placeholder even
  // when the pet has a species saved. Wait until both lists are loaded before
  // populating an existing pet.
  const optionsLoading = speciesQuery.isLoading || breedQuery.isLoading;

  useEffect(() => {
    if (!open) return;
    if (!initial) { reset(EMPTY); return; }
    if (optionsLoading) return;
    reset({
      name: initial.name || '',
      speciesId: String(initial.speciesId?._id || initial.speciesId || ''),
      breedId: String(initial.breedId?._id || initial.breedId || ''),
      sex: initial.sex || 'unknown',
      neutered: initial.neutered === true ? 'true' : initial.neutered === false ? 'false' : '',
      birthDate: dateInput(initial.birthDate),
      weightKg: initial.weightKg ?? '',
      microchip: initial.microchip || '',
      color: initial.color || '',
      allergies: initial.allergies || [],
      dietaryNotes: initial.dietaryNotes || '',
      medicalNotes: initial.medicalNotes || '',
      status: initial.status || 'active'
    });
  }, [open, initial, optionsLoading, reset]);

  // When the species changes, drop a breed that no longer belongs to it. On the
  // initial edit load the saved breed matches its species, so it is preserved.
  useEffect(() => {
    if (!watchedBreed) return;
    if (!filteredBreeds.some((b) => String(b._id) === String(watchedBreed))) {
      setValue('breedId', '');
    }
  }, [filteredBreeds, watchedBreed, setValue]);

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        name: values.name,
        speciesId: values.speciesId || null,
        breedId: values.breedId || null,
        sex: values.sex,
        neutered: values.neutered === '' ? null : values.neutered === 'true',
        birthDate: values.birthDate || null,
        weightKg: values.weightKg === '' ? null : Number(values.weightKg),
        microchip: values.microchip || undefined,
        color: values.color || undefined,
        allergies: values.allergies,
        dietaryNotes: values.dietaryNotes || undefined,
        medicalNotes: values.medicalNotes || undefined,
        status: values.status
      };
      for (const k of Object.keys(payload)) if (payload[k] === undefined) delete payload[k];
      if (editing) return petsApi.update(initial._id, payload);
      return petsApi.create({ ...payload, customerId });
    },
    onSuccess: () => {
      toast.success(editing ? 'Animale aggiornato.' : 'Animale creato.');
      queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'pets'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? `Modifica ${initial?.name}` : 'Nuovo animale'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="pet-form" type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="pet-form" className="flex flex-col gap-5" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Nome" error={errors.name?.message} {...register('name', { required: 'Nome richiesto' })} />
          <Select label="Stato" {...register('status')}>
            <option value="active">Attivo</option>
            <option value="archived">Archiviato</option>
            <option value="deceased">Deceduto</option>
          </Select>
          <Select label="Specie" {...register('speciesId')}>
            <option value="">-</option>
            {speciesOpts.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </Select>
          <Select label="Razza" {...register('breedId')} disabled={!watchedSpecies}>
            <option value="">{watchedSpecies ? '-' : 'Seleziona prima la specie'}</option>
            {filteredBreeds.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </Select>
          <Select label="Sesso" {...register('sex')}>
            <option value="unknown">Sconosciuto</option>
            <option value="male">Maschio</option>
            <option value="female">Femmina</option>
          </Select>
          <Select label="Sterilizzato" {...register('neutered')}>
            <option value="">Non specificato</option>
            <option value="true">Sì</option>
            <option value="false">No</option>
          </Select>
          <Input label="Data di nascita" type="date" {...register('birthDate')} />
          <Input label="Peso (kg)" type="number" step="0.1" min={0} {...register('weightKg')} />
          <Input label="Microchip" {...register('microchip')} />
          <Input label="Colore / mantello" {...register('color')} />
        </section>

        <Controller
          control={control}
          name="allergies"
          render={({ field }) => <TagInput label="Allergie" value={field.value} onChange={field.onChange} placeholder="Aggiungi e premi Invio" />}
        />

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Textarea label="Note alimentari" rows={3} {...register('dietaryNotes')} />
          <Textarea label="Note mediche" rows={3} {...register('medicalNotes')} />
        </section>
      </form>
    </Modal>
  );
}
